import { createContext, type ComponentChildren, type VNode } from 'preact';
import { useContext } from 'preact/hooks';

import { PageBar, Tabs } from '@amumin/ui';

import { PageHeader } from '../lib/ui.js';
import { familyLike, houseWords, sharedHouse } from '../state/householdType.js';
import { careOffered, moduleOn } from '../state/modules.js';
import { appText, context, navigate } from '../state/session.js';

// Bord en Gezin als één pagina met tabbladen (WP3, docs/ux/compact-plan.md hoofdstuk 3). Elk
// tabblad houdt zijn eigen adres (/board, /apparaten, /kamers, /fotolijst, /kindslot en /leden,
// /familie, /instellingen/zorg), zodat links uit e-mails en van het bord blijven werken; de kop is
// overal dezelfde: de titel van de pagina, de tabbladen eronder, en de knop van het tabblad rechts.

export type BoardTab = 'view' | 'tablets' | 'rooms' | 'frame' | 'kidLock';
export type FamilyTab = 'members' | 'links' | 'circle';
type Hub = 'board' | 'family';

const BOARD_HREF: Readonly<Record<BoardTab, string>> = {
  view: '/board',
  tablets: '/apparaten',
  rooms: '/kamers',
  frame: '/fotolijst',
  kidLock: '/kindslot',
};

const FAMILY_HREF: Readonly<Record<FamilyTab, string>> = {
  members: '/leden',
  links: '/familie',
  circle: '/instellingen/zorg',
};

function isParent(): boolean {
  const ctx = context.value;
  const role = ctx?.households.find((x) => x.id === ctx.active_household_id)?.role;
  return role === 'owner' || role === 'parent';
}

/** De tabbladen die deze gebruiker ziet (zelfde regels als de navigatie). */
function boardTabs(): Array<{ value: BoardTab; label: string }> {
  const s = appText.value.settingsHub.hubs.board;
  const out: Array<{ value: BoardTab; label: string }> = [];
  if (isParent()) out.push({ value: 'view', label: s.view });
  out.push({ value: 'tablets', label: s.tablets });
  if (moduleOn('rooms')) out.push({ value: 'rooms', label: s.rooms });
  if (moduleOn('photo_frame') && isParent()) out.push({ value: 'frame', label: s.frame });
  out.push({ value: 'kidLock', label: s.kidLock });
  return out;
}

function familyTabs(): Array<{ value: FamilyTab; label: string }> {
  const s = appText.value.settingsHub.hubs.family;
  const out: Array<{ value: FamilyTab; label: string }> = [{ value: 'members', label: s.members }];
  if (!sharedHouse.value) out.push({ value: 'links', label: s.links });
  if (isParent() && careOffered()) out.push({ value: 'circle', label: s.circle });
  return out;
}

export function hubTitle(hub: Hub): string {
  const s = appText.value.settingsHub.hubs;
  if (hub === 'board') return s.board.title;
  return familyLike.value ? s.family.title : houseWords.value.members;
}

const HubCtx = createContext<{ title: string; bar: VNode | null } | null>(null);

/**
 * De kop van een tabblad, met dezelfde props als PageHeader. In een hub: de titel van de hub, de
 * uitleg (ondertitel) achter de (i), de knop van het tabblad rechts en de tabbladen eronder. Buiten
 * een hub (bv. Schoonmaak, dat KamersPage deelt) gewoon PageHeader.
 */
export function HubPageHeader(props: Parameters<typeof PageHeader>[0]) {
  const h = useContext(HubCtx);
  if (!h) return <PageHeader {...props} />;
  return (
    <>
      <PageHeader
        title={h.title}
        info={props.info ?? props.subtitle}
        infoTitle={props.infoTitle ?? props.title}
        actions={props.actions}
        tools={props.tools}
        meta={props.meta ?? props.eyebrow}
        name={props.name}
      />
      {h.bar}
    </>
  );
}

/** Zet een tabblad in zijn hub. */
export function HubFrame<T extends string>({
  hub,
  tab,
  children,
}: {
  hub: Hub;
  tab: T;
  children: ComponentChildren;
}) {
  const s = appText.value.settingsHub.hubs;
  const items = (hub === 'board' ? boardTabs() : familyTabs()) as Array<{
    value: T;
    label: string;
  }>;
  const hrefs = (hub === 'board' ? BOARD_HREF : FAMILY_HREF) as Readonly<Record<string, string>>;
  const prefix = hub === 'board' ? 'board-hub' : 'family-hub';
  // Eén tabblad (bv. een kind ziet alleen Tablets en Kindslot is er altijd): dan toch de balk, zodat
  // de pagina overal hetzelfde oogt; zonder tabbladen geen balk.
  const bar =
    items.length > 1 ? (
      <PageBar name={`${prefix}-bar`}>
        <Tabs<T>
          idPrefix={prefix}
          label={hub === 'board' ? s.board.tabs : s.family.tabs}
          items={items}
          value={tab}
          onChange={(v) => navigate(hrefs[v] ?? '/', true)}
        />
      </PageBar>
    ) : null;
  return (
    <HubCtx.Provider value={{ title: hubTitle(hub), bar }}>
      <div id={`${prefix}-panel-${tab}`} data-hub={hub} data-hub-tab={tab}>
        {children}
      </div>
    </HubCtx.Provider>
  );
}

/** Een pagina als tabblad van een hub (voor een route). */
export function inHub(Page: () => ComponentChildren, hub: 'board', tab: BoardTab): () => VNode;
export function inHub(Page: () => ComponentChildren, hub: 'family', tab: FamilyTab): () => VNode;
export function inHub(Page: () => ComponentChildren, hub: Hub, tab: string) {
  return function InHub() {
    return (
      <HubFrame hub={hub} tab={tab}>
        <Page />
      </HubFrame>
    );
  };
}
